import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, Organization, App } from '@awang/shared-types'
import * as identityApi from '@/api/identity'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const org = ref<Organization | null>(null)
  const isLoggedIn = computed(() => !!user.value)

  function setUser(u: User): void {
    user.value = u
  }

  function setOrg(o: Organization): void {
    org.value = o
  }

  function setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
  }

  function clearTokens(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await identityApi.login({ email, password })
    user.value = res.user
    org.value = res.organization
    setTokens(res.access_token, res.refresh_token)
  }

  async function register(email: string, password: string, displayName?: string): Promise<void> {
    const res = await identityApi.register({ email, password, displayName })
    user.value = res.user
    org.value = res.organization
    setTokens(res.access_token, res.refresh_token)
  }

  async function fetchMe(): Promise<void> {
    const me = await identityApi.getMe()
    user.value = me
  }

  async function logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        await identityApi.logout(refreshToken)
      } catch {
        // 即使后端注销失败，也继续清理本地状态
      }
    }
    user.value = null
    org.value = null
    clearTokens()
  }

  return { user, org, isLoggedIn, setUser, setOrg, login, register, fetchMe, logout }
})

export const useAppStore = defineStore('app', () => {
  const subscribedApps = ref<App[]>([])
  const isLoading = ref(false)

  function setSubscribedApps(apps: App[]): void {
    subscribedApps.value = apps
  }

  return { subscribedApps, isLoading, setSubscribedApps }
})
